import {
  CaptionedBlockComponent,
  SelectedStyle,
} from '@blocksuite/affine-components/caption';
import {
  getAttachmentFileIcon,
  LoadingIcon,
} from '@blocksuite/affine-components/icons';
import { Peekable } from '@blocksuite/affine-components/peek';
import {
  type ResolvedStateInfo,
  ResourceController,
} from '@blocksuite/affine-components/resource';
import { toast } from '@blocksuite/affine-components/toast';
import {
  type AttachmentBlockModel,
  AttachmentBlockStyles,
} from '@blocksuite/affine-model';
import {
  BlockElementCommentManager,
  CitationProvider,
  createBlockIndexReviewLocator,
  DocModeProvider,
  EmbeddingBadgeController,
  type EmbeddingBadgeControllerHost,
  type EmbeddingIndexReviewLocator,
  FileSizeLimitProvider,
  TelemetryProvider,
  type UploadProgress,
  UploadProgressProvider,
} from '@blocksuite/affine-shared/services';
import { formatSize } from '@blocksuite/affine-shared/utils';
import {
  AttachmentIcon,
  ResetIcon,
  UpgradeIcon,
  WarningIcon,
} from '@blocksuite/icons/lit';
import { BlockSelection } from '@blocksuite/std';
import {
  type AttachmentMediaPresenceState,
  nanoid,
  Slice,
} from '@blocksuite/store';
import { BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE } from '@blocksuite/sync';
import { batch, computed, signal } from '@preact/signals-core';
import { html, type TemplateResult } from 'lit';
import { choose } from 'lit/directives/choose.js';
import { type ClassInfo, classMap } from 'lit/directives/class-map.js';
import { guard } from 'lit/directives/guard.js';
import { styleMap } from 'lit/directives/style-map.js';
import { when } from 'lit/directives/when.js';
import { filter } from 'rxjs/operators';

import { getAttachmentEmbedRenderGuardValues } from './attachment-embed-render-guard';
import {
  ATTACHMENT_MEDIA_PRESENCE_TARGET_SELECTOR,
  AttachmentEmbedProvider,
  type AttachmentMediaFollowOptions,
  type AttachmentMediaPresenceRemote,
  type AttachmentMediaPresenceTarget,
  type AttachmentMediaPresenceUpdate,
} from './embed';
import { styles } from './styles';
import {
  downloadAttachmentBlob,
  getAttachmentBlockExtension,
  refreshData,
  resolveAttachmentBlockDisplayName,
} from './utils';

const ATTACHMENT_MEDIA_PRESENCE_STALE_MS = 60_000;

type GfxViewportLike = {
  setViewportByBound: (
    bound: unknown,
    padding?: [number, number, number, number],
    smooth?: boolean
  ) => void;
};

type AttachmentResolvedStateInfo = ResolvedStateInfo & {
  kind?: TemplateResult;
};

@Peekable({
  enableOn: ({ model }: AttachmentBlockComponent) => {
    return !model.store.readonly && model.props.type.endsWith('pdf');
  },
})
export class AttachmentBlockComponent extends CaptionedBlockComponent<AttachmentBlockModel> {
  static override styles = styles;

  blockDraggable = true;

  resourceController = new ResourceController(
    computed(() => this.model.props.sourceId$.value)
  );

  /** Upload progress signal — non-null while this block's file is uploading. */
  private readonly _uploadProgress$ = computed<UploadProgress | null>(() => {
    const progressService = this.std.getOptional(UploadProgressProvider);
    if (!progressService) return null;
    return progressService.get(this.blockId).value;
  });

  /** File types that can have their text content extracted and embedded */
  private static readonly EMBEDDABLE_EXTENSIONS = new Set([
    'pdf',
    'html',
    'htm',
    'md',
    'markdown',
    'txt',
    'text',
    'csv',
    'json',
    'xml',
    'rtf',
    'doc',
    'docx',
  ]);

  private get _isEmbeddable(): boolean {
    return AttachmentBlockComponent.EMBEDDABLE_EXTENSIONS.has(
      this.filetype.toLowerCase()
    );
  }

  private get _usesEmbeddedPdfSearchStatus(): boolean {
    return (
      this.model.props.embed$.value === true &&
      this.model.props.type === 'application/pdf'
    );
  }

  private indexReviewLocator(): EmbeddingIndexReviewLocator | null {
    const blobId =
      this.model.props.sourceId$.value ?? this.model.props.sourceId;
    return createBlockIndexReviewLocator({
      workspaceId: this.store.workspace.id,
      docId: this.store.id,
      subjectKind: 'attachment-block',
      blockId: this.model.id,
      blobId,
    });
  }

  private readonly _embeddingBadge = new EmbeddingBadgeController(
    this as unknown as EmbeddingBadgeControllerHost,
    {
      canRender: () => this._isEmbeddable && !this._usesEmbeddedPdfSearchStatus,
      canTrack: () => this._isEmbeddable,
      getIndexReviewLocator: () => this.indexReviewLocator(),
      idlePollMs: 30_000,
      includeWorkspaceProgress: true,
      mediaType: () => this.model.props.type,
      notify: message => toast(this.host, message),
    }
  );

  get blobUrl() {
    return this.resourceController.blobUrl$.value;
  }

  get filetype() {
    return getAttachmentBlockExtension(this.model);
  }

  protected containerStyleMap = styleMap({
    position: 'relative',
    width: '100%',
    margin: '18px 0px',
  });

  private get _maxFileSize() {
    return this.std.get(FileSizeLimitProvider).maxFileSize;
  }

  get citationService() {
    return this.std.get(CitationProvider);
  }

  get isCitation() {
    return this.citationService.isCitationModel(this.model);
  }

  get isCommentHighlighted() {
    return (
      this.std
        .getOptional(BlockElementCommentManager)
        ?.isBlockCommentHighlighted(this.model) ?? false
    );
  }

  convertTo = () => {
    return this.std
      .get(AttachmentEmbedProvider)
      .convertTo(this.model, this._maxFileSize);
  };

  copy = () => {
    const slice = Slice.fromModels(this.store, [this.model]);
    this.std.clipboard.copySlice(slice).catch(console.error);
    toast(this.host, 'Copied to clipboard');
  };

  download = () => {
    downloadAttachmentBlob(this);
  };

  embedded = () => {
    return (
      Boolean(this.blobUrl) &&
      this.std
        .get(AttachmentEmbedProvider)
        .embedded(this.model, this._maxFileSize)
    );
  };

  open = () => {
    const blobUrl = this.blobUrl;
    if (!blobUrl) return;
    window.open(blobUrl, '_blank');
  };

  // Refreshes data.
  refreshData = () => {
    refreshData(this).catch(console.error);
  };

  private readonly _refreshKey$ = signal<string | null>(null);

  private _mediaPresenceCleanupTimer: number | null = null;

  private _mediaPresencePill:
    | HTMLElementTagNameMap['affine-attachment-media-presence-pill']
    | null = null;

  private _mediaFollowTarget: { clientId: number; muted: boolean } | null =
    null;

  private _suppressMediaPresencePublish = false;

  private _mediaPresenceSetup = false;

  // Refreshes the embed component.
  reload = () => {
    batch(() => {
      if (this.model.props.embed$.value) {
        this._refreshKey$.value = nanoid();
        return;
      }

      this.refreshData();
    });
  };

  private get _isMediaAttachment() {
    const type = this.model.props.type;
    return type.startsWith('audio/') || type.startsWith('video/');
  }

  private get _mediaAwarenessScope() {
    return `attachment-media:${this.store.id}`;
  }

  private get _isLocalModerating() {
    return (
      this.std.store.awarenessStore.getLocalState()?.presentation
        ?.isPresenting === true
    );
  }

  private _selectBlock() {
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create(BlockSelection, {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  private readonly _publishMediaPresence = (
    update: AttachmentMediaPresenceUpdate
  ) => {
    if (!this.isConnected || !this._isMediaAttachment) {
      return;
    }

    if (this._mediaFollowTarget) {
      this._clearLocalMediaPresence({ anyAttachment: true });
      return;
    }

    if (this._suppressMediaPresencePublish) {
      if (update.action === 'time') {
        return;
      }
      this._suppressMediaPresencePublish = false;
    }

    const updatedAt = Date.now();
    const state: AttachmentMediaPresenceState = {
      ...update,
      attachmentId: this.blockId,
      moderated: this._isLocalModerating,
      name: resolveAttachmentBlockDisplayName(this.model),
      scope: this._mediaAwarenessScope,
      sourceId: this.model.props.sourceId$.value,
      updatedAt,
    };
    this.std.store.awarenessStore.setLocalStateField(
      'attachmentMediaPresence',
      state
    );
  };

  private _clearLocalMediaPresence(options: { anyAttachment?: boolean } = {}) {
    const current =
      this.std.store.awarenessStore.getLocalState()?.attachmentMediaPresence;
    if (!current) {
      return;
    }

    if (
      !options.anyAttachment &&
      (current.scope !== this._mediaAwarenessScope ||
        current.attachmentId !== this.blockId)
    ) {
      return;
    }

    this.std.store.awarenessStore.setLocalStateField(
      'attachmentMediaPresence',
      undefined
    );
  }

  private _clearMediaPresenceCleanupTimer() {
    if (this._mediaPresenceCleanupTimer === null) return;

    window.clearTimeout(this._mediaPresenceCleanupTimer);
    this._mediaPresenceCleanupTimer = null;
  }

  private _removeMediaPresencePill() {
    this._clearMediaPresenceCleanupTimer();
    this._mediaPresencePill?.remove();
    this._mediaPresencePill = null;
  }

  private readonly _focusMediaBlock = () => {
    this._selectBlock();

    const gfx = (this as unknown as { gfx?: { viewport?: GfxViewportLike } })
      .gfx;
    const bound = (
      this.model as AttachmentBlockModel & { elementBound?: unknown }
    ).elementBound;

    if (gfx?.viewport?.setViewportByBound && bound) {
      gfx.viewport.setViewportByBound(bound, [80, 80, 80, 80], true);
      return;
    }

    this.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  };

  private _getMediaPresenceTarget() {
    return this.querySelector(
      `affine-media-attachment-embed, ${ATTACHMENT_MEDIA_PRESENCE_TARGET_SELECTOR}`
    ) as (Element & Partial<AttachmentMediaPresenceTarget>) | null;
  }

  private _applyRemoteMediaPresence(
    presence: AttachmentMediaPresenceState,
    options: AttachmentMediaFollowOptions,
    focus: boolean
  ) {
    if (focus) {
      this._focusMediaBlock();
    }

    this._getMediaPresenceTarget()?.applyRemotePresence?.(presence, options);
  }

  private _followRemoteMedia(
    remote: AttachmentMediaPresenceRemote,
    options: AttachmentMediaFollowOptions
  ) {
    this._mediaFollowTarget = {
      clientId: remote.clientId,
      muted: options.muted,
    };
    this._suppressMediaPresencePublish = true;
    this._clearLocalMediaPresence({ anyAttachment: true });
    this._applyRemoteMediaPresence(remote.presence, options, true);
    this._showMediaPresencePill(remote);
  }

  private readonly _leaveRemoteMedia = () => {
    this._mediaFollowTarget = null;
    this._suppressMediaPresencePublish = true;
    this._clearLocalMediaPresence({ anyAttachment: true });
    this._updateRemoteMediaPresence();
  };

  private _isFollowingRemoteMedia(remote: AttachmentMediaPresenceRemote) {
    return this._mediaFollowTarget?.clientId === remote.clientId;
  }

  private _getMediaPresencePillContainer() {
    const edgelessRoot = this.closest<HTMLElement>('affine-edgeless-root');
    const widgetsContainer =
      edgelessRoot?.querySelector<HTMLElement>('.widgets-container') ?? null;
    if (widgetsContainer) {
      return { canvasScoped: true, container: widgetsContainer };
    }

    const edgelessViewport =
      this.closest<HTMLElement>('.affine-edgeless-viewport') ??
      this.host.closest<HTMLElement>('.affine-edgeless-viewport');
    if (edgelessViewport) {
      return { canvasScoped: true, container: edgelessViewport };
    }

    return { canvasScoped: false, container: document.body };
  }

  private _showMediaPresencePill(remote: AttachmentMediaPresenceRemote) {
    if (!this._mediaPresencePill) {
      const pill = document.createElement(
        'affine-attachment-media-presence-pill'
      );
      pill.onFocusMedia = this._focusMediaBlock;
      pill.onFollowMedia = options => {
        const current = this._mediaPresencePill?.remote;
        if (!current) return;
        this._followRemoteMedia(current, options);
      };
      pill.onLeaveMedia = this._leaveRemoteMedia;
      this._mediaPresencePill = pill;
    }

    const { canvasScoped, container } = this._getMediaPresencePillContainer();
    this._mediaPresencePill.canvasScoped = canvasScoped;
    if (this._mediaPresencePill.parentElement !== container) {
      container.append(this._mediaPresencePill);
    }

    this._mediaPresencePill.remote = remote;
    this._mediaPresencePill.following = this._isFollowingRemoteMedia(remote);
    this._clearMediaPresenceCleanupTimer();
    const expiresAt =
      remote.presence.updatedAt + ATTACHMENT_MEDIA_PRESENCE_STALE_MS;
    const delay = Math.max(1000, expiresAt - Date.now() + 50);
    this._mediaPresenceCleanupTimer = window.setTimeout(() => {
      this._updateRemoteMediaPresence();
    }, delay);
  }

  private _updateRemoteMediaPresence() {
    if (!this.isConnected || !this._isMediaAttachment) {
      this._removeMediaPresencePill();
      return;
    }

    const awarenessStore = this.std.store.awarenessStore;
    const localClientId = awarenessStore.awareness.clientID;
    const now = Date.now();
    const sourceId = this.model.props.sourceId$.value;
    const followTarget = this._mediaFollowTarget;
    let next: AttachmentMediaPresenceRemote | null = null;

    awarenessStore.getStates().forEach((state, clientId) => {
      if (clientId === localClientId) return;
      if (followTarget && clientId !== followTarget.clientId) return;

      const presence = state.attachmentMediaPresence;
      if (
        !presence ||
        presence.scope !== this._mediaAwarenessScope ||
        presence.attachmentId !== this.blockId ||
        now - presence.updatedAt > ATTACHMENT_MEDIA_PRESENCE_STALE_MS
      ) {
        return;
      }

      if (presence.sourceId && sourceId && presence.sourceId !== sourceId) {
        return;
      }

      if (!presence.playing && !followTarget) {
        return;
      }

      if (!next || presence.updatedAt > next.presence.updatedAt) {
        next = {
          clientId,
          color: state.color,
          presence,
          userName: state.user?.name ?? 'Someone',
        };
      }
    });

    const nextRemote = next as AttachmentMediaPresenceRemote | null;
    if (!nextRemote) {
      if (this._mediaFollowTarget) {
        this._mediaFollowTarget = null;
        this._suppressMediaPresencePublish = true;
        this._clearLocalMediaPresence({ anyAttachment: true });
      }
      this._removeMediaPresencePill();
      return;
    }

    if (this._isFollowingRemoteMedia(nextRemote)) {
      this._applyRemoteMediaPresence(
        nextRemote.presence,
        { muted: this._mediaFollowTarget?.muted ?? false },
        false
      );
    }

    this._showMediaPresencePill(nextRemote);
  }

  private _setupMediaPresenceAwareness() {
    if (
      this._mediaPresenceSetup ||
      !this.isConnected ||
      !this._isMediaAttachment
    ) {
      return;
    }

    this._mediaPresenceSetup = true;
    const updateRemoteMediaPresence = () => this._updateRemoteMediaPresence();
    const awareness = this.std.store.awarenessStore.awareness;
    awareness.on('change', updateRemoteMediaPresence);
    this.disposables.add(() => {
      awareness.off('change', updateRemoteMediaPresence);
      this._mediaPresenceSetup = false;
      this._removeMediaPresencePill();
      this._clearLocalMediaPresence();
    });
    updateRemoteMediaPresence();
  }

  private readonly _trackCitationDeleteEvent = () => {
    // Check citation delete event
    this._disposables.add(
      this.std.store.slots.blockUpdated
        .pipe(
          filter(payload => {
            if (!payload.isLocal) return false;

            const { flavour, id, type } = payload;
            if (
              type !== 'delete' ||
              flavour !== this.model.flavour ||
              id !== this.model.id
            )
              return false;

            const { model } = payload;
            if (!this.citationService.isCitationModel(model)) return false;

            return true;
          })
        )
        .subscribe(() => {
          this.citationService.trackEvent('Delete');
        })
    );
  };

  override connectedCallback() {
    super.connectedCallback();

    this.contentEditable = 'false';

    this.resourceController.setEngine(this.std.store.blobSync);

    this.disposables.add(this.resourceController.subscribe());
    this.disposables.add(this.resourceController);

    // Existing/materialized blobs do not emit a sourceId change after mount.
    // Resolve them immediately so view conversion and previews are available
    // without requiring the user to press Reload first.
    void this.refreshData();

    this.disposables.add(
      this.model.props.sourceId$.subscribe(() => {
        this.refreshData();
      })
    );

    if (!this.model.props.style && !this.store.readonly) {
      this.store.withoutTransact(() => {
        this.store.updateBlock(this.model, {
          style: AttachmentBlockStyles[1],
        });
      });
    }

    this._trackCitationDeleteEvent();

    this._embeddingBadge.connect();
    this._setupMediaPresenceAwareness();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._embeddingBadge.disconnect();
    this._removeMediaPresencePill();
    this._clearLocalMediaPresence();
  }

  override firstUpdated() {
    // lazy bindings
    this.disposables.addFromEvent(this, 'click', this.onClick);
    this.disposables.addFromEvent(this, 'dblclick', this._onDoubleClick, {
      capture: true,
    });
  }

  protected onClick(event: MouseEvent) {
    // the peek view need handle shift + click
    if (event.defaultPrevented) return;

    event.stopPropagation();

    if (!this.selected$.peek()) {
      this._selectBlock();
    }
  }

  private readonly _onDoubleClick = (event: MouseEvent) => {
    if (event.defaultPrevented) return;
    if (this.model.props.type !== 'application/pdf') return;
    if (this.model.props.embed$.value) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest('button, [role="menu"], input, textarea, select')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    window.dispatchEvent(
      new CustomEvent('streamlain:attachment-fullscreen', {
        detail: {
          attachmentId: this.model.id,
        },
      })
    );
  };

  protected renderUpgradeButton = () => {
    if (this.std.store.readonly) return null;

    const onOverFileSize = this.std.get(FileSizeLimitProvider).onOverFileSize;

    return when(
      onOverFileSize,
      () => html`
        <button
          class="affine-attachment-content-button"
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            onOverFileSize?.();

            {
              const mode =
                this.std.get(DocModeProvider).getEditorMode() ?? 'page';
              const segment = mode === 'page' ? 'doc' : 'whiteboard';
              this.std
                .getOptional(TelemetryProvider)
                ?.track('AttachmentUpgradedEvent', {
                  segment,
                  page: `${segment} editor`,
                  module: 'attachment',
                  control: 'upgrade',
                  category: 'card',
                  type: getAttachmentBlockExtension(this.model),
                });
            }
          }}
        >
          ${UpgradeIcon()} Upgrade
        </button>
      `
    );
  };

  protected renderNormalButton = (needUpload: boolean) => {
    const label = needUpload ? 'retry' : 'reload';
    const run = async () => {
      if (needUpload) {
        await this.resourceController.upload();
        return;
      }

      this.refreshData();
    };

    return html`
      <button
        class="affine-attachment-content-button"
        @click=${(event: MouseEvent) => {
          event.stopPropagation();
          run().catch(console.error);

          {
            const mode =
              this.std.get(DocModeProvider).getEditorMode() ?? 'page';
            const segment = mode === 'page' ? 'doc' : 'whiteboard';
            this.std
              .getOptional(TelemetryProvider)
              ?.track('AttachmentReloadedEvent', {
                segment,
                page: `${segment} editor`,
                module: 'attachment',
                control: label,
                category: 'card',
                type: this.filetype,
              });
          }
        }}
      >
        ${ResetIcon()} ${label}
      </button>
    `;
  };

  protected renderWithHorizontal(
    classInfo: ClassInfo,
    {
      icon,
      title,
      description,
      kind,
      state,
      needUpload,
    }: AttachmentResolvedStateInfo
  ) {
    return html`
      <div class=${classMap(classInfo)}>
        <div class="affine-attachment-content">
          <div class="affine-attachment-content-title">
            <div class="affine-attachment-content-title-icon">${icon}</div>
            <div class="affine-attachment-content-title-text truncate">
              ${title}
            </div>
          </div>

          <div class="affine-attachment-content-description">
            <div class="affine-attachment-content-info truncate">
              ${description}
            </div>
            ${choose(state, [
              ['error', () => this.renderNormalButton(needUpload)],
              ['error:oversize', this.renderUpgradeButton],
            ])}
          </div>
        </div>

        <div class="affine-attachment-banner">${kind}</div>
      </div>
    `;
  }

  protected renderWithVertical(
    classInfo: ClassInfo,
    {
      icon,
      title,
      description,
      kind,
      state,
      needUpload,
    }: AttachmentResolvedStateInfo
  ) {
    return html`
      <div class=${classMap(classInfo)}>
        <div class="affine-attachment-content">
          <div class="affine-attachment-content-title">
            <div class="affine-attachment-content-title-icon">${icon}</div>
            <div class="affine-attachment-content-title-text truncate">
              ${title}
            </div>
          </div>

          <div class="affine-attachment-content-info truncate">
            ${description}
          </div>
        </div>

        <div class="affine-attachment-banner">
          ${kind}
          ${choose(state, [
            ['error', () => this.renderNormalButton(needUpload)],
            ['error:oversize', this.renderUpgradeButton],
          ])}
        </div>
      </div>
    `;
  }

  protected resolvedState$ = computed<AttachmentResolvedStateInfo>(() => {
    const size = this.model.props.size;
    const name = resolveAttachmentBlockDisplayName(this.model);
    const kind = getAttachmentFileIcon(this.filetype);
    const progress = this._uploadProgress$.value;

    // While uploading (no sourceId yet), show uploading state with progress
    if (progress) {
      const pct = Math.round(progress.ratio * 100);
      return {
        loading: true,
        error: false,
        state: 'uploading' as const,
        needUpload: false,
        url: null,
        icon: LoadingIcon(),
        title: name,
        description: `${formatSize(size)} · ${pct}%`,
        kind,
      };
    }

    const resolvedState = this.resourceController.resolveStateWith({
      loadingIcon: LoadingIcon(),
      errorIcon: WarningIcon(),
      icon: AttachmentIcon(),
      title: name,
      description: formatSize(size),
    });

    return { ...resolvedState, kind };
  });

  private readonly renderProgressBar = () => {
    const progress = this._uploadProgress$.value;
    if (!progress) return null;
    const pct = Math.round(progress.ratio * 100);

    return html`
      <div class="affine-attachment-progress-bar">
        <div
          class="affine-attachment-progress-bar-fill"
          style=${styleMap({ width: `${pct}%` })}
        ></div>
      </div>
    `;
  };

  // sl-amc0: Render embedding status badge (indexed / not indexed / loading)
  private readonly renderEmbeddingBadge = () => this._embeddingBadge.render();

  protected renderCardView = () => {
    const resolvedState = this.resolvedState$.value;
    const cardStyle = this.model.props.style$.value ?? AttachmentBlockStyles[1];

    const classInfo = {
      'affine-attachment-card': true,
      [cardStyle]: true,
      'local-storage-unavailable':
        resolvedState.errorCode === BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE,
      loading: resolvedState.loading,
      error: resolvedState.error,
    };

    return html`
      ${when(
        cardStyle === 'cubeThick',
        () => this.renderWithVertical(classInfo, resolvedState),
        () => this.renderWithHorizontal(classInfo, resolvedState)
      )}
      ${this.renderProgressBar()}
    `;
  };

  protected renderEmbedView = () => {
    const { model, blobUrl } = this;
    if (!model.props.embed$.value) return null;

    const { std, _maxFileSize } = this;
    const provider = std.get(AttachmentEmbedProvider);

    const render = provider.getRender(model, _maxFileSize);
    if (!render) return null;
    if (!blobUrl && !provider.canRenderWithoutBlob(model, _maxFileSize)) {
      return null;
    }

    const enabled = provider.shouldShowStatus(model);
    const selected = this.selected$.value;
    const interactive = this.embedInteractive;
    const rerenderOnContextChange =
      provider.shouldRerenderOnContextChange(model);
    const guardValues = getAttachmentEmbedRenderGuardValues({
      blobUrl: blobUrl ?? '',
      interactive,
      refreshKey: this._refreshKey$.value,
      rerenderOnContextChange,
      selected,
    });

    return html`
      <div class="affine-attachment-embed-container">
        ${guard(guardValues, () =>
          render(model, blobUrl ?? '', {
            interactive,
            loadBlob: () => this.resourceController.blob(),
            publishMediaPresence: this._isMediaAttachment
              ? this._publishMediaPresence
              : undefined,
            selected,
            std,
          })
        )}
      </div>
      ${when(enabled, () => {
        const resolvedState = this.resolvedState$.value;
        if (resolvedState.state !== 'error') return null;
        // It should be an error messge.
        const message = resolvedState.description;
        if (!message) return null;

        const needUpload = resolvedState.needUpload;
        const action = () =>
          needUpload ? this.resourceController.upload() : this.reload();

        return html`
          <affine-resource-status
            class="affine-attachment-embed-status"
            .message=${message}
            .needUpload=${needUpload}
            .action=${action}
          ></affine-resource-status>
        `;
      })}
    `;
  };

  private readonly _renderCitation = () => {
    const { name, footnoteIdentifier } = this.model.props;
    const icon = getAttachmentFileIcon(this.filetype);

    return html`<affine-citation-card
      .icon=${icon}
      .citationTitle=${name}
      .citationIdentifier=${footnoteIdentifier}
      .active=${this.selected$.value}
    ></affine-citation-card>`;
  };

  override renderBlock() {
    return html`
      <div
        class=${classMap({
          'affine-attachment-container': true,
          focused: this.selected$.value,
          'comment-highlighted': this.isCommentHighlighted,
        })}
        style=${this.containerStyleMap}
      >
        ${when(
          this.isCitation,
          () => this._renderCitation(),
          () => this.renderEmbedView() ?? this.renderCardView()
        )}
        ${this.renderEmbeddingBadge()}
      </div>
    `;
  }

  protected get embedInteractive() {
    return this.selected$.value;
  }

  override accessor selectedStyle = SelectedStyle.Border;

  override accessor useCaptionEditor = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-attachment': AttachmentBlockComponent;
  }
}
